import { ImageOff } from 'lucide-react'

import BarChart from 'features/DemographicsCharts/components/BarChart'
import PieChart from 'features/DemographicsCharts/components/PieChart'
import { buildDemographicCharts, type AreaFigures, type AreaKey } from 'features/DemographicsCharts/utils/chartData'
import { cn } from 'lib/utils'

import { EmptyBlock } from '../EmptyBlock'
import { asArray, asNumber, asText } from '../payload'

/**
 * Renderers for the location elements (MYD-499). Payload in, JSX out, like the
 * generic ones: the shapes mirror backend/presentations/providers/
 * locationElements.js, where every value is already formatted for display.
 *
 * Sizes are logical pixels on the 1280x720 stage. Every renderer tolerates a
 * missing or malformed payload, and says so plainly when the location simply
 * has no data yet (no photos, no tenants), which is common and not an error.
 */

/** What a block shows when the location has nothing for it yet. */
const Empty = EmptyBlock

/** A small heading over a list or chart, in the same voice as the facts table's title bar. */
function Caption({ children }: { children: React.ReactNode }) {
	return <p className="mb-3 text-[16px] font-semibold uppercase tracking-wide text-primary">{children}</p>
}

// ---------------------------------------------------------------------------

export function LocationCoverBlock({ payload }: { payload: any }) {
	const image = asText(payload?.image)
	const logo = asText(payload?.logo)
	const type = asText(payload?.type)
	const address = [asText(payload?.address), asText(payload?.place)].filter(Boolean).join(' · ')

	return (
		// Square corners: a cover is usually full-bleed, behind a Title slide.
		<div className={cn('relative h-full min-h-[240px] overflow-hidden', image ? 'bg-gray-900' : 'bg-gradient-to-br from-primary to-blue-900')}>
			{image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" draggable={false} />}
			<div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
			{logo && (
				<div className="absolute right-8 top-8 flex h-24 w-24 items-center justify-center rounded-xl bg-white p-3 shadow-lg">
					<img src={logo} alt="" className="max-h-full max-w-full object-contain" draggable={false} />
				</div>
			)}
			<div className="absolute inset-x-0 bottom-0 p-10 text-white">
				{type && <p className="mb-2 text-[18px] font-medium uppercase tracking-wider text-white/80">{type}</p>}
				<h2 className="text-[56px] font-bold leading-tight">{asText(payload?.name)}</h2>
				{address && <p className="mt-2 text-[24px] text-white/90">{address}</p>}
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------

/** The facts table, after the DetailsTable look: a tinted title bar over hairline-ruled rows. */
export function LocationFactsBlock({ payload }: { payload: any }) {
	const rows = asArray<any>(payload?.rows)
	const title = asText(payload?.title)
	if (rows.length === 0) return <Empty>No facts recorded for this location yet.</Empty>

	return (
		<div className="overflow-hidden rounded-xl border border-gray-200">
			{title && <div className="bg-primary/10 px-6 py-3 text-[16px] font-semibold uppercase tracking-wider text-primary">{title}</div>}
			<dl>
				{rows.map((row, index) => (
					<div key={index} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6 border-b border-gray-100 px-6 py-3.5 last:border-b-0">
						<dt className="truncate text-[20px] text-gray-500">{asText(row?.label)}</dt>
						<dd className="truncate text-right text-[22px] font-medium text-gray-900">{asText(row?.value)}</dd>
					</div>
				))}
			</dl>
		</div>
	)
}

// ---------------------------------------------------------------------------

export function LocationTenantsBlock({ payload }: { payload: any }) {
	const tenants = asArray<any>(payload?.tenants)
	const total = asNumber(payload?.total) ?? tenants.length
	const showSales = payload?.showSales !== false

	if (payload?.isCenter === false) return <Empty>Tenant rosters are kept for centers.</Empty>
	if (tenants.length === 0) return <Empty>No tenants recorded for this center yet.</Empty>

	return (
		<div>
			<table className="w-full table-fixed text-left">
				<thead>
					<tr className="border-b-2 border-gray-200 text-[16px] uppercase tracking-wide text-gray-500">
						<th className="py-2 pr-4 font-semibold">Tenant</th>
						<th className="w-[28%] py-2 pr-4 text-right font-semibold">Size</th>
						{showSales && <th className="w-[28%] py-2 text-right font-semibold">Sales (est.)</th>}
					</tr>
				</thead>
				<tbody>
					{tenants.map((tenant, index) => (
						<tr key={index} className="border-b border-gray-100 text-[20px]">
							<td className="truncate py-2.5 pr-4 font-medium text-gray-900">{asText(tenant?.name)}</td>
							<td className="py-2.5 pr-4 text-right text-gray-700">{asText(tenant?.size)}</td>
							{showSales && <td className="py-2.5 text-right text-gray-700">{asText(tenant?.sales)}</td>}
						</tr>
					))}
				</tbody>
			</table>
			{total > tenants.length && <p className="mt-3 text-[16px] text-gray-500">and {total - tenants.length} more</p>}
		</div>
	)
}

// ---------------------------------------------------------------------------

/** A payload may name its own subject (a project's photos use this grid). */
function NoPhoto({ message }: { message?: string }) {
	return (
		<Empty fill>
			<span className="inline-flex items-center gap-2">
				<ImageOff className="h-6 w-6" /> {message || 'No photos of this location yet.'}
			</span>
		</Empty>
	)
}

export function LocationPhotoBlock({ payload }: { payload: any }) {
	const url = asText(payload?.url)
	const caption = asText(payload?.caption)
	if (!url) return <NoPhoto />

	return (
		<figure className="flex h-full min-h-0 flex-col gap-3">
			<div className="min-h-0 flex-1 overflow-hidden rounded-xl bg-gray-100">
				<img src={url} alt={caption} className={cn('h-full w-full', payload?.fit === 'contain' ? 'object-contain' : 'object-cover')} draggable={false} />
			</div>
			{caption && <figcaption className="text-[18px] text-gray-500">{caption}</figcaption>}
		</figure>
	)
}

/** Columns and rows for 1 to 6 photos, so no count leaves a lone orphan on a row by itself. */
const GRID: Record<number, string> = {
	1: 'grid-cols-1',
	2: 'grid-cols-2',
	3: 'grid-cols-3',
	4: 'grid-cols-2 grid-rows-2',
	5: 'grid-cols-3 grid-rows-2',
	6: 'grid-cols-3 grid-rows-2',
}

export function LocationPhotoGridBlock({ payload }: { payload: any }) {
	const photos = asArray<any>(payload?.photos)
		.filter(item => asText(item?.url))
		.slice(0, 6)
	if (photos.length === 0) return <NoPhoto message={asText(payload?.empty)} />

	return (
		<div className={cn('grid h-full min-h-0 gap-3', GRID[photos.length])}>
			{photos.map((item, index) => (
				<figure
					key={index}
					// Five photos: the first takes two rows, so the grid stays full.
					className={cn('relative min-h-0 overflow-hidden rounded-xl bg-gray-100', photos.length === 5 && index === 0 && 'row-span-2')}
				>
					<img src={asText(item.url)} alt={asText(item?.caption)} className="h-full w-full object-cover" draggable={false} />
					{asText(item?.caption) && (
						<figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-8 text-[16px] text-white">
							{asText(item.caption)}
						</figcaption>
					)}
				</figure>
			))}
		</div>
	)
}

// ---------------------------------------------------------------------------

const AREA_NAMES: Record<AreaKey, string> = { postalCode: 'Postal code', city: 'City', cbsa: 'CBSA' }

const VIEW_TITLES: Record<string, string> = {
	population: 'Population',
	households: 'Households',
	incomeVsHomeValue: 'Income vs home price',
	gender: 'Gender',
	ethnicity: 'Ethnicity',
	table: 'Demographics',
}

const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const COUNT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const AGE = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })

const TABLE_ROWS: Array<{ label: string; field: keyof AreaFigures; format: Intl.NumberFormat }> = [
	{ label: 'Population', field: 'population', format: COUNT },
	{ label: 'Households', field: 'householdsPerZipCode', format: COUNT },
	{ label: 'Median household income', field: 'incomePerHousehold', format: MONEY },
	{ label: 'Average home value', field: 'averageHouseValue', format: MONEY },
	{ label: 'Median age', field: 'medianAge', format: AGE },
]

const isArea = (value: unknown): value is AreaKey => value === 'postalCode' || value === 'city' || value === 'cbsa'

/**
 * Drop the areas a bar chart has no figures for. On a slide an empty CBSA
 * column reads as "zero", or as something broken; many locations have no CBSA.
 */
const withoutEmptyAreas = <T extends { labels: string[]; datasets: Array<{ data: Array<number | undefined> }> }>(chart: T): T => {
	const keep = chart.labels.map((_, index) => chart.datasets.some(dataset => typeof dataset.data[index] === 'number'))
	return {
		...chart,
		labels: chart.labels.filter((_, index) => keep[index]),
		datasets: chart.datasets.map(dataset => ({ ...dataset, data: dataset.data.filter((_, index) => keep[index]) })),
	}
}

/**
 * One of the Demographics tab's charts, drawn by the same components from the
 * same builder, or a table of one area's figures.
 */
export function LocationDemographicsBlock({ payload }: { payload: any }) {
	const raw = payload?.areas && typeof payload.areas === 'object' ? payload.areas : {}
	const areas = { postalCode: raw.postalCode ?? null, city: raw.city ?? null, cbsa: raw.cbsa ?? null }
	const labels = {
		postalCode: asText(payload?.labels?.postalCode) || 'Postal code',
		city: asText(payload?.labels?.city) || 'City',
		cbsa: asText(payload?.labels?.cbsa) || 'CBSA',
	}
	const view = asText(payload?.view) || 'population'
	const area: AreaKey = isArea(payload?.area) ? payload.area : 'postalCode'

	if (!areas.postalCode && !areas.city && !areas.cbsa) return <Empty fill>No demographics are available for this location.</Empty>

	const oneArea = view === 'gender' || view === 'ethnicity' || view === 'table'
	if (oneArea && !areas[area]) return <Empty fill>No demographics for this {AREA_NAMES[area].toLowerCase()}.</Empty>

	const title = oneArea ? `${VIEW_TITLES[view] ?? 'Demographics'} · ${labels[area]}` : VIEW_TITLES[view] ?? 'Demographics'

	if (view === 'table') {
		const figures = areas[area] as AreaFigures
		return (
			<div>
				<Caption>{title}</Caption>
				<dl className="overflow-hidden rounded-xl border border-gray-200">
					{TABLE_ROWS.map(row => {
						const value = asNumber(figures[row.field])
						return (
							<div key={row.field} className="flex justify-between gap-6 border-b border-gray-100 px-6 py-3.5 last:border-b-0">
								<dt className="text-[20px] text-gray-500">{row.label}</dt>
								<dd className="text-[22px] font-medium text-gray-900">{value === null ? '—' : row.format.format(value)}</dd>
							</div>
						)
					})}
				</dl>
			</div>
		)
	}

	const charts = buildDemographicCharts(areas, labels)
	const chart =
		view === 'gender' ? (
			<PieChart data={charts.gender[area]} fill fontSize={18} />
		) : view === 'ethnicity' ? (
			<PieChart data={charts.ethnicity[area]} fill fontSize={18} />
		) : (
			<BarChart
				data={withoutEmptyAreas(view === 'households' ? charts.households : view === 'incomeVsHomeValue' ? charts.incomeVsHomePrice : charts.population)}
				options={{ showLegend: view === 'incomeVsHomeValue' }}
				fill
				fontSize={18}
			/>
		)

	return (
		<div className="flex h-full min-h-0 flex-col">
			<Caption>{title}</Caption>
			<div className="relative min-h-0 flex-1">{chart}</div>
		</div>
	)
}

// ---------------------------------------------------------------------------

/** A brand's logo on white, or its monogram on its own colour, as on the map. */
function BrandChip({ retailer }: { retailer: any }) {
	const logo = asText(retailer?.logoUrl)
	if (logo) {
		return (
			<div className="flex h-20 w-20 items-center justify-center rounded-xl border border-gray-200 bg-white p-2.5">
				<img src={logo} alt="" className="max-h-full max-w-full object-contain" draggable={false} />
			</div>
		)
	}
	return (
		<div
			className="flex h-20 w-20 items-center justify-center rounded-xl text-[30px] font-semibold text-white"
			style={{ background: asText(retailer?.monogramColor) || '#4A5568' }}
		>
			{asText(retailer?.monogram) || asText(retailer?.name).charAt(0).toUpperCase()}
		</div>
	)
}

export function LocationOtherRetailersBlock({ payload }: { payload: any }) {
	const retailers = asArray<any>(payload?.retailers)
	const total = asNumber(payload?.total) ?? retailers.length
	const radius = asNumber(payload?.radiusMiles)
	const within = radius === null ? '' : ` within ${radius} mi`

	if (retailers.length === 0) return <Empty>No saved retailers{within}.</Empty>

	return (
		<div>
			<Caption>
				{total} {total === 1 ? 'brand' : 'brands'}
				{within}
			</Caption>
			<ul className="grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))] gap-x-4 gap-y-5">
				{retailers.map((retailer, index) => (
					<li key={index} className="flex flex-col items-center text-center">
						<BrandChip retailer={retailer} />
						<span className="mt-2 w-full truncate text-[17px] font-medium text-gray-900">{asText(retailer?.name)}</span>
						<span className="text-[15px] text-gray-500">{asText(retailer?.distance)}</span>
					</li>
				))}
			</ul>
			{total > retailers.length && <p className="mt-4 text-[16px] text-gray-500">and {total - retailers.length} more</p>}
		</div>
	)
}

// ---------------------------------------------------------------------------

export function LocationNotesBlock({ payload }: { payload: any }) {
	const notes = asArray<any>(payload?.notes)
	if (notes.length === 0) return <Empty>No notes on this location yet.</Empty>

	return (
		<ul className="space-y-4">
			{notes.map((note, index) => (
				<li key={index} className="rounded-xl border-l-4 border-primary bg-gray-50 px-6 py-4">
					<p className="line-clamp-4 text-[20px] leading-snug text-gray-900">{asText(note?.text)}</p>
					<p className="mt-2 text-[16px] text-gray-500">{[asText(note?.author), asText(note?.date)].filter(Boolean).join(' · ')}</p>
				</li>
			))}
		</ul>
	)
}

export function LocationProjectsBlock({ payload }: { payload: any }) {
	const projects = asArray<any>(payload?.projects)
	const total = asNumber(payload?.total) ?? projects.length
	if (projects.length === 0) return <Empty>No active projects at this location.</Empty>

	return (
		<div>
			<ul className="divide-y divide-gray-100">
				{projects.map((project, index) => (
					<li key={index} className="py-3">
						<p className="text-[22px] font-semibold text-gray-900">{asText(project?.name)}</p>
						<p className="text-[17px] text-gray-500">
							{[asText(project?.type), asText(project?.owner), asText(project?.started) && `since ${asText(project.started)}`].filter(Boolean).join(' · ')}
						</p>
					</li>
				))}
			</ul>
			{total > projects.length && <p className="mt-3 text-[16px] text-gray-500">and {total - projects.length} more</p>}
		</div>
	)
}
